import "./Insight.scss";
import "./DashboardGlobal.scss";
import { useEffect, useState } from "react";
import { useContent } from "../../context/ContentContext";
import Score from "./Score";
import { useNavigate } from "react-router-dom";
import { Panel } from "../ui";

/**
 * OPTIMIZED: This component now uses synchronous environment lookups
 * from the pre-fetched environment data in ContentContext.
 * 
 * Before: Each Insight made an async API call to fetch environment data.
 * After: Environment names are looked up instantly from the cached data.
 */
const Insight = ({ insight }) => {
  const { getThumbnail } = useContent();
  const navigate = useNavigate();
  const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);
  const [thumbnail, setThumbnail] = useState("");

  const showMissingThumbnail = !thumbnail || thumbnailLoadFailed;

  useEffect(() => {
    let cancelled = false;

    setThumbnail("");
    setThumbnailLoadFailed(false);

    const environmentId = insight.environmentId;
    if (!environmentId) return () => {
      cancelled = true;
    };

    getThumbnail(environmentId)
      .then((url) => {
        if (!cancelled) {
          setThumbnail(url || "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setThumbnail("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getThumbnail, insight.environmentId]);

  const goToQuestion = () => {
    const insightId = insight?.insightId ?? "";
    const q = insight?.textValue ?? "";
    const experienceId = insight?.experienceId ?? "";

    // Include experienceId in URL for even faster loading on QuestionAnswersPage
    let url = `/question?insightId=${encodeURIComponent(insightId)}&q=${encodeURIComponent(q)}`;
    if (experienceId) {
      url += `&experienceId=${encodeURIComponent(experienceId)}`;
    }
    
    navigate(url);
  };

  const onRowKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToQuestion();
    }
  };

  return (
    <Panel
      className="grid-row dashboard-row insight"
      interactive
      onClick={goToQuestion}
      role="button"
      tabIndex={0}
      onKeyDown={onRowKeyDown}
      aria-label="Open insight details"
    >
      <div className="grid-column medium environment insight-environment">
        {showMissingThumbnail ? (
          <div className="thumbnail thumbnail--missing" role="img" aria-label="Environment thumbnail not found">
            <span className="thumbnailMissingLabel">Not Found</span>
          </div>
        ) : (
          <img
            src={thumbnail}
            className="thumbnail"
            alt="Environment"
            onError={() => {
              setThumbnailLoadFailed(true);
            }}
          />
        )}
      </div>

      <div className="grid-column wide question insight-question">
        {insight.textValue}
      </div>

      <div
        className="grid-column medium answers insight-metric insight-metric--completed"
        data-label="Completed"
      >
        <h2>{insight.responseCount}</h2>
      </div>

      <div className="grid-column narrow insight-metric insight-metric--score" data-label="Score">
        <Score score={insight.meanScore} />
      </div>
    </Panel>
  );
};

export default Insight;
